/* eslint-disable no-nested-ternary */
/* eslint-disable func-names */
/* eslint-disable fp/no-mutation */
/* eslint-disable react/prop-types */
import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {useInView} from 'react-intersection-observer';

import '../../assets/scss/components/gallery.scss';

const SMALLEST_WIDTH = 200; /* px */

const loadedImages = new Set();

function MasonryImage({widest, ratio, orientation, name, lqipSrc, src}) {
  const [preloading, setPreloading] = useState(lqipSrc && !loadedImages.has(src));
  const [ref, inView] = useInView({triggerOnce: true});

  useEffect(() => {
    if (!inView || loadedImages.has(src)) return () => null;
    if (lqipSrc) {
      setPreloading(true);
      const img = new Image();
      img.onload = () => {
        setPreloading(false);
        loadedImages.add(src);
      };
      img.src = src;
    }
    return () => null;
  }, [inView, src, lqipSrc]);

  let flex = 1 / (widest / ratio);
  flex = flex !== 0 ? flex : 1;
  const orientationClass = orientation;
  const width = SMALLEST_WIDTH * flex;

  return (
    <a className="gallery-figure" style={{flex, minWidth: width}} href={src} target="_blank" rel="noopener noreferrer" ref={ref}>
      <img
        className={`gallery-image ${orientationClass}`}
        src={preloading ? lqipSrc : src}
        alt={name}
        style={{width: '100%', height: 'auto', filter: `blur(${preloading ? '50' : '0'}px)`}}
      />
    </a>
  );
}

MasonryImage.propTypes = {
  src: PropTypes.string.isRequired,
  ratio: PropTypes.number.isRequired,
  widest: PropTypes.number.isRequired,
  orientation: PropTypes.string.isRequired,
};

function MasonryVideo({
  src,
  widest,
  ratio,
  thumbSrc = null,
  lqipSrc = null,
  mp4Src = null,
  isProcessing = false,
  hasError = false,
  isTimeout = false,
  isTooLarge = false,
  conversionError = null,
}) {
  // Ensure we can stream/play assets even if backend stored them with alt=attachment
  const normalizeMediaAlt = url => {
    if (!url || typeof url !== 'string') return url;
    try {
      const u = new URL(url);
      if (u.searchParams.get('alt') === 'attachment') {
        u.searchParams.set('alt', 'media');
        return u.toString();
      }
      return url;
    } catch (e) {
      return url;
    }
  };

  const streamingMp4Src = normalizeMediaAlt(mp4Src);
  const displayThumbSrc = normalizeMediaAlt(thumbSrc);
  const [preloading, setPreloading] = useState(lqipSrc && !loadedImages.has(displayThumbSrc));
  const [ref, inView] = useInView({triggerOnce: true});

  useEffect(() => {
    if (!inView || !displayThumbSrc || loadedImages.has(displayThumbSrc)) return () => null;
    if (lqipSrc) {
      setPreloading(true);
      const img = new Image();
      img.onload = () => {
        setPreloading(false);
        loadedImages.add(displayThumbSrc);
      };
      img.src = displayThumbSrc;
    }
    return () => null;
  }, [inView, displayThumbSrc, lqipSrc]);

  let flex = 1 / (widest / ratio) || 1;
  flex = flex !== 0 ? flex : 1;
  const width = SMALLEST_WIDTH * flex;

  const renderContent = () => {
    if (streamingMp4Src && streamingMp4Src !== false) {
      return (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          controls
          className="gallery-video"
          poster={preloading ? lqipSrc : displayThumbSrc}
          preload={displayThumbSrc ? 'none' : 'metadata'}
          style={{filter: `blur(${preloading ? '50' : '0'}px)`}}>
          <source src={streamingMp4Src} type="video/mp4" />
          <a href={src} target="_blank" rel="noopener noreferrer">
            View the video
          </a>
        </video>
      );
    }

    if (isTooLarge || (isTimeout && !mp4Src) || (hasError && !isTooLarge && !isTimeout)) {
      return (
        <div className="gallery-video-loading">
          <div style={{textAlign: 'center', padding: '1em'}}>
            <a href={src} target="_blank" rel="noopener noreferrer" style={{display: 'block'}}>
              Download original video to view
            </a>
          </div>
        </div>
      );
    }

    if (isProcessing) {
      return (
        <div className="gallery-video-loading">
          <span>Video is processing...</span>
        </div>
      );
    }

    return (
      <div className="gallery-video-loading">
        <a href={src} target="_blank" rel="noopener noreferrer">
          The video is ready to be downloaded and viewed
        </a>
      </div>
    );
  };

  return (
    <div className="gallery-figure" style={{flex, minWidth: width}} ref={ref}>
      {renderContent()}
    </div>
  );
}

MasonryVideo.propTypes = {
  src: PropTypes.string.isRequired,
  thumbSrc: PropTypes.string,
  lqipSrc: PropTypes.string,
  mp4Src: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isProcessing: PropTypes.bool,
  hasError: PropTypes.bool,
  isTimeout: PropTypes.bool,
  isTooLarge: PropTypes.bool,
  widest: PropTypes.number.isRequired,
  ratio: PropTypes.number.isRequired,
  conversionError: PropTypes.string,
};

const load = ({
  type,
  postFilePublic: url,
  postFilePublicMp4: mp4Url,
  postFilePublicLqip: lqipUrl,
  postFilePublicThumb: thumbUrl,
  conversionError,
  processing,
  conversionStatus,
  conversionStartedAt,
  conversionDeadlineAt,
  conversionEndedAt,
  conversionErrorCode,
  sourceSizeBytes,
  sourceSizeLimitBytes,
}) => {
  if (type.match(/video.*/)) {
    return new Promise((resolve, reject) => {
      const now = Date.now();
      const hasErrorExplicit = mp4Url === false || mp4Url === 'false';
      const status = conversionStatus || (hasErrorExplicit ? 'error' : mp4Url ? 'success' : processing ? 'pending' : 'pending');
      let isProcessing = status === 'pending';
      const timeoutGraceMs = 10000;
      if (isProcessing && conversionDeadlineAt && now > conversionDeadlineAt + timeoutGraceMs) {
        isProcessing = false; // infer timeout
      }
      const hasError = status === 'error' || status === 'timeout' || hasErrorExplicit;
      const isTimeout = status === 'timeout' || (conversionDeadlineAt && now > conversionDeadlineAt + timeoutGraceMs && !mp4Url);
      const isTooLarge = conversionErrorCode === 'SOURCE_TOO_LARGE' || (sourceSizeLimitBytes && sourceSizeBytes > sourceSizeLimitBytes);

      if (thumbUrl && lqipUrl) {
        const img = new Image();
        img.onload = () => {
          resolve({
            url,
            mp4Url,
            thumbUrl,
            lqipUrl,
            type,
            isProcessing,
            hasError,
            isTimeout,
            isTooLarge,
            conversionError,
            ratio: img.naturalWidth / img.naturalHeight,
          });
        };
        img.src = lqipUrl;
        return;
      }

      const video = document.createElement('video');
      video.addEventListener(
        'loadedmetadata',
        function () {
          // send back result
          resolve({
            url,
            mp4Url,
            thumbUrl,
            lqipUrl,
            type,
            isProcessing,
            hasError,
            isTimeout,
            isTooLarge,
            conversionError,
            ratio: this.videoWidth / this.videoHeight,
          });
        },
        false
      );
      video.src = url;
    });
  }

  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({
        url,
        type,
        lqipUrl,
        orientation: img.naturalWidth > img.naturalHeight ? 'landscape' : 'portrait',
        ratio: img.naturalWidth / img.naturalHeight,
        ratioHeight: img.naturalHeight / img.naturalWidth,
      });
    };

    img.src = lqipUrl || url;
  });
};

const descentOrder = (a, b) => {
  const ratioA = a.ratio;
  const ratioB = b.ratio;

  return ratioA === ratioB ? 0 : ratioA < ratioB ? 1 : -1;
};

function MasonryGallery({mode, files}) {
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [widest, setWidest] = useState(null);

  useEffect(() => {
    Promise.all(files.map(load))
      // eslint-disable-next-line fp/no-mutating-methods
      .then(ratios => ratios.sort(descentOrder))
      .then(newGalleryFiles => {
        setGalleryFiles(newGalleryFiles);
        setWidest(newGalleryFiles.length ? newGalleryFiles[0].ratio : null);
      });
  }, [files, setGalleryFiles, setWidest]);

  return (
    <div className="gallery show">
      {galleryFiles.map(file => {
        switch (file.type.split('/')[0]) {
          case 'image':
            return (
              <MasonryImage
                key={file.url}
                src={file.url}
                lqipSrc={mode === 'pdf' ? null : file.lqipUrl}
                ratio={file.ratio}
                widest={widest}
                orientation={file.orientation}
                ratioHeight={file.ratioHeight}
              />
            );
          case 'video':
            if (mode === 'pdf') {
              return (
                <a key={file.url} className="gallery-figure" href={file.url} target="_blank" rel="noopener noreferrer">
                  <img className="gallery-image landscape" src={file.thumbUrl} alt={file.url} />
                </a>
              );
            }
            return (
              <MasonryVideo
                key={file.url}
                src={file.url}
                mp4Src={file.mp4Url}
                thumbSrc={file.thumbUrl}
                lqipSrc={mode === 'pdf' ? null : file.lqipUrl}
                ratio={file.ratio}
                widest={widest}
                type={file.type}
                isProcessing={file.isProcessing}
                hasError={file.hasError}
                isTimeout={file.isTimeout}
                isTooLarge={file.isTooLarge}
                conversionError={file.conversionError}
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

MasonryGallery.propTypes = {
  mode: PropTypes.string.isRequired,
  files: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      postFilePublic: PropTypes.string.isRequired,
      postFilePublicMp4: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      postFilePublicOgg: PropTypes.string,
      postFilePublicLqip: PropTypes.string,
      postFilePublicThumb: PropTypes.string,
    })
  ).isRequired,
};

export default MasonryGallery;
